import Component from '@glimmer/component';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { fn } from '@ember/helper';
import { t } from 'ember-intl';
import { dropTask } from 'ember-concurrency';
import type { IntlService } from 'ember-intl';
import type Owner from '@ember/owner';
import type RouterService from '@ember/routing/router-service';
import { formatCurrency } from 'tio-ui/format';
import { PhX, PhCheck, PhClock } from 'ember-phosphor-icons';
import type { ComponentLike } from '@glint/template';
import { getSingleValueForTasField } from 'tio-common/utils/tuition-assistance/fields';
import Button from 'tio-ui/components/button';
import Drawer, { DrawerBody } from 'tio-ui/components/drawer';
import CourseCashTag from 'tio-common/components/tas/course-cash-tag';
import PaymentDetailsCard from 'tio-common/components/tas/payment-details-card';
import ActivityView from 'tio-common/components/tas/course-cash/activity-view';
import ReceiptUploadModal from 'tio-employee/components/tas/course-cash/receipt-upload-modal';
import NeedsYourAttention from './needs-your-attention';
import RejectionDetail from 'tio-employee/components/reimagined/tas/rejection-detail';
import ShowCourseDrawer from 'tio-employee/components/tas/show-course-drawer';
import { deriveAttention } from 'tio-employee/utils/tas/attention-tasks';
import {
  fetchCourseCashTransactions,
  refreshCourseCashTransactions,
  uploadCourseCashReceipt,
  removeCourseCashReceipt,
} from 'tio-employee/utils/course-cash/receipt-actions';
import {
  saveCourseGrade,
  saveApplicationGpa,
  saveAgreementsForConditions,
  submitEvidenceForApproval,
} from 'tio-employee/utils/tas/evidence-actions';
import type { SubmitEvidenceBundle } from './task-card/submit-evidence';
import type { TasCourseExtension as TasCourse } from 'tio-data/data/resources/tas-course.ext';
import type {
  AttentionModel,
  AttentionTask,
  AttentionTaskKind,
} from 'tio-employee/utils/tas/attention-tasks';
import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';
import type { Store as StoreService } from '@warp-drive/core';
import type DigitalAssetsService from 'tio-common/services/digital-assets';
import type { DigitalAssetDetailSignature } from 'tio-common/services/digital-assets';
import type {
  CourseCashReceipt,
  CourseCashTransaction,
} from 'tio-common/types/course-cash-transaction';
import type SessionContextService from 'tio-employee/services/session-context';
import type TuitionAssistanceService from 'tio-common/services/tuition-assistance';
import type { ConditionExtension as ConditionModel } from 'tio-data/data/resources/condition.ext';
import type { ErrorsSignature } from 'tio-ui/components/error-messages';

// A receipt still needs attention until it's verified or explicitly not required.
const UNRESOLVED_RECEIPT_STATUSES = new Set(['needed', 'rejected', 'changes_needed']);

// Mirrors show.gts's local type of the same name.
type LockEmployeeStatusBasedOnType =
  | 'COURSE_START_DATE'
  | 'COURSE_END_DATE'
  | 'FULFILLED_DATE'
  | 'ON_COURSES_SUBMISSION'
  | 'ON_SUBMISSION';

/** Narrows a caught error to what ErrorMessages can render — a JSON:API-style
 * `{ errors: [...] }` document (the common `store.request` failure shape) or
 * a plain string message. */
function toDisplayError(error: unknown): string | ErrorsSignature {
  if (
    typeof error === 'object' &&
    error !== null &&
    'errors' in error &&
    Array.isArray((error as { errors: unknown }).errors)
  ) {
    return error as ErrorsSignature;
  }
  return error instanceof Error ? error.message : String(error);
}

interface ManagePageSignature {
  Args: {
    application: TasApplicationModel;
    /** From the route (see manage-route.ts) — undefined in contexts with no
     * route model (dev fixture gallery), where the eligibility lock simply
     * doesn't apply. */
    dynamicEligibility?: boolean;
    programEligibility?: string[];
  };
  Element: HTMLElement;
}

type RailIcon = ComponentLike<{ Args: { size: number }; Element: SVGElement }>;

interface RailTaskRow {
  label: string;
  statusKey: string;
  icon: RailIcon;
  completed: boolean;
}

const RAIL_TASK_LABEL_KEYS: Record<AttentionTaskKind, string> = {
  'use-course-cash': 'tuition_assistance.applications.manage.rail.task_use_course_cash',
  'upload-receipt': 'tuition_assistance.applications.manage.rail.task_upload_receipt',
  'submit-evidence': 'tuition_assistance.applications.manage.rail.task_submit_evidence',
};

// Amount rows are hidden until courses are pre-approved (see
// shouldDisplayApprovalAmounts).
const PRE_APPROVAL_STATES = new Set([
  'TAS.ApplicationState.CREATED',
  'TAS.ApplicationState.DEFINE_COURSES',
  'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
  'TAS.ApplicationState.COURSES_NOT_APPROVED',
  'TAS.ApplicationState.COURSES_REJECTED',
]);

// Final approval has actually happened (see finalApprovedLabel).
const POST_FINAL_APPROVAL_STATES = new Set([
  'TAS.ApplicationState.EVIDENCE_APPROVED',
  'TAS.ApplicationState.FULFILLED',
]);

// Mirrors RejectionDetail's own state list (see isRejected) so the right-rail
// Tasks fallback doesn't contradict the RejectionDetail banner it sits next to.
const REJECTED_STATES = new Set([
  'TAS.ApplicationState.COURSES_NOT_APPROVED',
  'TAS.ApplicationState.COURSES_REJECTED',
  'TAS.ApplicationState.EVIDENCE_NOT_APPROVED',
  'TAS.ApplicationState.EVIDENCE_REJECTED',
]);

const RAIL_STATUS_KEYS: Record<AttentionTask['status'], string> = {
  pending: 'tuition_assistance.applications.manage.rail.status_pending',
  'in-progress': 'tuition_assistance.applications.manage.rail.status_in_progress',
  completed: 'tuition_assistance.applications.manage.rail.status_completed',
};

// TZ-safe MM/DD/YY from a YYYY-MM-DD(THH:..) string (see attention-tasks chip note).
function shortDate(value: string | null | undefined): string {
  if (!value) {
    return '';
  }
  const [year, month, day] = (value.split('T')[0] ?? '').split('-');
  if (!year || !month || !day) {
    return '';
  }
  return `${month}/${day}/${year.slice(2)}`;
}

function divideBy100(value: number): number {
  return (value ?? 0) / 100;
}

// getSingleValueForTasField returns the broad FieldValue union; narrow to a
// string instead of casting, since a non-string field shape should not crash
// rendering or surface "[object Object]".
function fieldAsString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

/**
 * The TAS course-management ("manage") screen: a clean header, the Needs Your
 * Attention task panel + request summary on the left, and a right rail (Tasks,
 * Request Details, Disbursement, Latest Comment). Reachable behind the
 * `tasCourseManagement` flag (see utils/tas/manage-route).
 */
export default class ManagePage extends Component<ManagePageSignature> {
  @service declare intl: IntlService;
  @service declare router: RouterService;
  @service declare store: StoreService;
  @service declare digitalAssets: DigitalAssetsService;
  @service declare sessionContext: SessionContextService;
  @service declare tuitionAssistance: TuitionAssistanceService;

  @tracked isCourseDrawerOpen = false;
  @tracked courseShowing: TasCourse | null = null;
  @tracked isActivityDrawerOpen = false;
  @tracked courseCashTransactions: CourseCashTransaction[] = [];
  @tracked isReceiptModalOpen = false;
  @tracked acceptedEvidenceConditions: ConditionModel[] = [];
  @tracked evidenceSubmitError: string | ErrorsSignature | null = null;
  @tracked selectedReceiptTransaction: CourseCashTransaction | null = null;

  constructor(owner: Owner, args: ManagePageSignature['Args']) {
    super(owner, args);
    this.loadCourseCashTransactions();
  }

  get application(): TasApplicationModel {
    return this.args.application;
  }

  /** Load Course Cash transactions from the approvables endpoint + assistance data. */
  loadCourseCashTransactions = async () => {
    this.courseCashTransactions = await fetchCourseCashTransactions(this.store, this.application);
  };

  reloadCourseCashTransactions = async () => {
    this.courseCashTransactions = await refreshCourseCashTransactions(this.store, this.application);
  };

  /**
   * Every live transaction still needing a receipt — one upload-receipt task
   * per swipe (Figma). Optional-chained: manage-page-attention-model-test.ts
   * exercises `attentionModel` on a class instance built via `Object.create`
   * (no constructor run, so `courseCashTransactions` was never initialized).
   */
  get unresolvedReceiptTransactions(): CourseCashTransaction[] {
    return (this.courseCashTransactions ?? []).filter((txn) =>
      UNRESOLVED_RECEIPT_STATUSES.has(txn.receiptStatus)
    );
  }

  get hasLiveCourseCashData(): boolean {
    return (this.application.tasAssistances?.length ?? 0) > 0;
  }

  /**
   * Single-task fallback for contexts with no live assistance data (dev
   * fixture gallery, unit tests — see the TODO atop attention-tasks.ts; the
   * real TasApplicationModel has no receiptUploaded field of its own). Once
   * there IS live data, `attentionModel` uses `receiptTasks` instead and
   * ignores this.
   */
  get receiptUploadedFallback(): boolean {
    const fixtureFlag = (this.application as unknown as { receiptUploaded?: boolean })
      .receiptUploaded;
    return Boolean(fixtureFlag);
  }

  openReceiptModal = (transaction: CourseCashTransaction) => {
    this.selectedReceiptTransaction = transaction;
    this.isReceiptModalOpen = true;
  };

  /**
   * @onUploadReceipt for NeedsYourAttention — each upload-receipt task's id is
   * `upload-receipt-<transactionId>` (see attention-tasks.ts), so strip the
   * prefix to find the transaction this specific card's button was clicked for.
   */
  openReceiptModalForTask = (task: AttentionTask) => {
    const transactionId = task.id.replace(/^upload-receipt-/, '');
    const transaction = this.courseCashTransactions.find((t) => t.id === transactionId);
    if (transaction) {
      this.openReceiptModal(transaction);
    }
  };

  closeReceiptModal = () => {
    this.isReceiptModalOpen = false;
    this.selectedReceiptTransaction = null;
  };

  handleReceiptUpload = async (
    files: DigitalAssetDetailSignature[],
    transaction: CourseCashTransaction
  ) => {
    await uploadCourseCashReceipt(
      this.store,
      this.digitalAssets,
      this.application,
      files,
      transaction
    );
    await this.reloadCourseCashTransactions();
  };

  handleRemoveReceipt = async (receipt: CourseCashReceipt, transaction: CourseCashTransaction) => {
    await removeCourseCashReceipt(this.store, receipt);
    await this.reloadCourseCashTransactions();

    const updatedTransaction = this.courseCashTransactions.find((t) => t.id === transaction.id);
    if (updatedTransaction) {
      this.selectedReceiptTransaction = updatedTransaction;
    }
  };

  /**
   * Evidence submission — mirrors the legacy show page's grade modal +
   * attachments section + AcceptConditions + submitForEvidenceApproval (see
   * `tio-employee/app/utils/tas/evidence-actions.ts`), wired inline instead of
   * routing the "Submit Evidence" task out to the guided wizard.
   */

  get isActiveParticipant(): boolean {
    return this.application.tasProgramInstance?.employee?.tasParticipant?.isActive ?? false;
  }

  get conditionsForEvidence(): readonly ConditionModel[] {
    return (
      this.application.tasProgramInstance?.tasProgramTemplate
        ?.applicationApproveEvidenceConditions ?? []
    );
  }

  get hasAgreedAllEvidenceConditions(): boolean {
    if (!this.conditionsForEvidence.length) {
      return true;
    }
    return this.conditionsForEvidence.length === this.acceptedEvidenceConditions.length;
  }

  /** Mirrors the legacy page's showGPAField — only Type 3/4.3 templates configured to calculate the total by GPA. */
  get showGPAField(): boolean {
    const typeClassification = this.application.typeClassification;
    return Boolean(
      this.application.tasProgramInstance?.tasProgramTemplate?.calculateTotalByGPA &&
      (typeClassification === 'TAS.ProgramType.3' || typeClassification === 'TAS.ProgramType.4.3')
    );
  }

  get hasGpaIfRequired(): boolean {
    if (!this.showGPAField) {
      return true;
    }
    return Boolean(getSingleValueForTasField('GRADE_POINT_AVERAGE', this.application.fields));
  }

  /** Mirrors the legacy page's lockStatus. */
  get lockStatus(): string {
    const lockType: Record<LockEmployeeStatusBasedOnType, string> = {
      COURSE_START_DATE: 'start',
      COURSE_END_DATE: 'end',
      FULFILLED_DATE: 'fulfilled',
      ON_COURSES_SUBMISSION: 'submission',
      ON_SUBMISSION: 'submission',
    };
    const lockEmployeeStatusBasedOn = this.application.tasProgramInstance?.tasProgramTemplate
      ?.lockEmployeeStatusBasedOn as LockEmployeeStatusBasedOnType | undefined;
    return lockEmployeeStatusBasedOn && lockEmployeeStatusBasedOn in lockType
      ? lockType[lockEmployeeStatusBasedOn]
      : '';
  }

  /** Mirrors the legacy page's startOrEndDate. */
  get startOrEndDate(): unknown {
    if (this.lockStatus === 'start') {
      return this.application.coursesBeginDate;
    }
    if (this.lockStatus === 'end') {
      return fieldAsString(getSingleValueForTasField('COURSES_END_DATE', this.application.fields));
    }
    return new Date().toLocaleDateString('en-CA');
  }

  /** Mirrors the legacy page's calculateEligibility. */
  calculateEligibility(): boolean {
    const programEligibility = this.args.programEligibility ?? [];
    if (!programEligibility.length) {
      return false;
    }
    const dateValue = this.startOrEndDate;
    if (!dateValue || typeof dateValue !== 'string') {
      return false;
    }
    const enteredDate = new Date(dateValue);
    return programEligibility.some((period) => {
      const [start, end] = period as unknown as [string, string];
      return enteredDate >= new Date(start) && enteredDate <= new Date(end);
    });
  }

  /**
   * Mirrors the legacy page's inEligible — the policy-lock check driven by
   * dynamic eligibility + eligibility periods (both fetched by
   * manage-route.ts, the same way the legacy show route fetches them).
   */
  get inEligible(): boolean {
    const app = this.application;
    if (app.ignorePolicies || !this.lockStatus) {
      return false;
    }
    const completedStates = [
      'TAS.ApplicationState.FULFILLED',
      'TAS.ApplicationState.ABANDONED',
      'TAS.ApplicationState.REJECTED_PER_POLICY',
    ];
    if (completedStates.includes(app.state)) {
      return false;
    }
    if (['fulfilled', 'submission'].includes(this.lockStatus)) {
      return false;
    }
    return !(this.args.dynamicEligibility && this.calculateEligibility());
  }

  /**
   * Mirrors the legacy page's canSubmitApplication.
   */
  get canSubmitEvidence(): boolean {
    const app = this.application;
    if (app.ignorePolicies) {
      return true;
    }
    const sharedConditions =
      this.isActiveParticipant &&
      this.hasAgreedAllEvidenceConditions &&
      app.hasRequiredEvidenceAttachments &&
      !this.inEligible &&
      app.state === 'TAS.ApplicationState.ATTEND' &&
      this.hasGpaIfRequired;
    return app.isCourseGradeRequired
      ? sharedConditions && !app.isMissingCourseGrades
      : sharedConditions;
  }

  get evidenceSubmissionBundle(): SubmitEvidenceBundle {
    return {
      onSaveGrade: this.handleSaveCourseGrade,
      onSaveGpa: this.handleSaveGpa,
      onAcceptConditions: this.handleAcceptEvidenceConditions,
      onSubmit: () => this.submitEvidence.perform(),
      isSubmitting: this.submitEvidence.isRunning,
      submitError: this.evidenceSubmitError,
      canSubmit: this.canSubmitEvidence,
    };
  }

  handleSaveCourseGrade = async (course: TasCourse, grade: string) => {
    await saveCourseGrade(this.store, course, grade);
  };

  handleSaveGpa = async (gpa: number) => {
    await saveApplicationGpa(this.store, this.tuitionAssistance, this.application, gpa);
  };

  handleAcceptEvidenceConditions = (conditions: ConditionModel[]) => {
    this.acceptedEvidenceConditions = conditions;
  };

  submitEvidence = dropTask(async () => {
    this.evidenceSubmitError = null;
    try {
      await saveAgreementsForConditions(
        this.store,
        this.sessionContext.user,
        this.acceptedEvidenceConditions
      );
      await submitEvidenceForApproval(this.store, this.sessionContext, this.application);
    } catch (error) {
      console.error('Failed to submit evidence:', error);
      this.evidenceSubmitError = toDisplayError(error);
    }
  });

  get pcsCards() {
    return this.application.pcsCards ?? [];
  }

  get pcsCardsWithMeta() {
    const cards = this.pcsCards;
    return cards.map((card, i) => ({ card, cardNumber: i + 1, totalCards: cards.length }));
  }

  openActivityDrawer = () => {
    this.isActivityDrawerOpen = true;
  };

  closeActivityDrawer = () => {
    this.isActivityDrawerOpen = false;
  };

  get programTemplate() {
    return this.application.tasProgramInstance?.tasProgramTemplate;
  }

  openCourseDrawer = (course: TasCourse) => {
    this.courseShowing = course;
    this.isCourseDrawerOpen = true;
  };

  /**
   * Routes back into the guided wizard for this application — used both to fix
   * a rejected/changes-requested application (RejectionDetail's CTA) and to
   * submit evidence (the Submit Evidence task's CTA), mirroring
   * reimagined/tas/applications/show.gts's navigateToWizard.
   */
  navigateToWizard = () => {
    const typeClass = this.programTemplate?.typeClassification;
    let routeSuffix = 'type-two-guided';
    if (typeClass === 'TAS.ProgramType.3') {
      routeSuffix = 'type-three-guided';
    } else if (typeClass?.startsWith('TAS.ProgramType.4')) {
      routeSuffix = 'type-four-guided';
    }
    this.router.transitionTo(
      `authenticated.tas.applications.${routeSuffix}.new`,
      this.application.id
    );
  };

  closeCourseDrawer = () => {
    this.isCourseDrawerOpen = false;
    this.courseShowing = null;
  };

  get courseDrawerHeader(): string {
    return this.courseShowing?.displayName ?? '';
  }

  get attentionModel(): AttentionModel {
    const app = this.application;
    return deriveAttention({
      state: app.state,
      amounts: app.amounts as Parameters<typeof deriveAttention>[0]['amounts'],
      isEvidenceRequired: app.isEvidenceRequired,
      isMissingRequiredEvidence: app.isMissingRequiredEvidence,
      isCourseGradeRequired: app.isCourseGradeRequired,
      isMissingCourseGrades: app.isMissingCourseGrades,
      isApproved: app.isApproved,
      // Same state-gated total the rail uses (elevator-pitch#210): the raw
      // coalescing approvedTotal (FINAL ?? PRE) reads as a real final number
      // before final approval has actually happened, which is exactly the
      // "Your approval total is $0.00" confusion the banner needs to avoid.
      approvedTotal: this.hasPassedFinalApproval
        ? (app.finalApprovedTotal ?? 0)
        : (app.preApprovedTotal ?? 0),
      receiptTasks: this.hasLiveCourseCashData
        ? this.unresolvedReceiptTransactions.map((txn) => ({
            id: txn.id,
            dueDate: txn.receiptDueDate ?? null,
            status: txn.receiptStatus,
            amount: txn.amount,
            transactionDate: txn.transactionDate,
          }))
        : undefined,
      receiptUploaded: this.receiptUploadedFallback,
    });
  }

  get programName(): string {
    return this.application.tasProgramInstance?.tasProgramTemplate?.programName ?? '';
  }

  get institutionName(): string {
    return (
      fieldAsString(
        getSingleValueForTasField(
          'SCHOOL_INSTITUTION_NAME',
          this.application.tasProgramInstance?.fields
        )
      ) ?? ''
    );
  }

  get submittedLabel(): string {
    const date = shortDate(this.application.applicationSubmittedDate);
    return date
      ? this.intl.t('tuition_assistance.applications.manage.header.submitted_on', { date })
      : '';
  }

  get isDirectPayProgram(): boolean {
    return this.application.tasProgramInstance?.tasProgramTemplate?.isDirectPayProgram ?? false;
  }

  get datesLabel(): string {
    const start = shortDate(this.application.coursesBeginDate);
    const end = shortDate(
      fieldAsString(getSingleValueForTasField('COURSES_END_DATE', this.application.fields))
    );
    return start && end ? `${start}–${end}` : start || end;
  }

  get requestedLabel(): string {
    return formatCurrency((this.application.requestedTotal ?? 0) / 100);
  }

  get preApprovedLabel(): string {
    return formatCurrency((this.application.preApprovedTotal ?? 0) / 100);
  }

  /**
   * The backend mirrors PRE_APPROVED_TOTAL into FINAL_APPROVED_TOTAL on every
   * save until final approval overwrites it (TasApplication
   * #sync_approved_totals_if_applicable), so the raw field cannot distinguish
   * "not finally approved yet" — only the state can. Read $0.00 until the
   * application has actually passed final (evidence) approval.
   */
  get finalApprovedLabel(): string {
    if (!this.hasPassedFinalApproval) {
      return formatCurrency(0);
    }
    return formatCurrency((this.application.finalApprovedTotal ?? 0) / 100);
  }

  get hasPassedFinalApproval(): boolean {
    return POST_FINAL_APPROVAL_STATES.has(this.application.state);
  }

  get isRejected(): boolean {
    return REJECTED_STATES.has(this.application.state);
  }

  /**
   * The approval amount rows appear once courses preapproval has happened:
   * hidden in CREATED and every pre-approval state, visible from
   * COURSES_APPROVED onward. This deliberately diverges from the model's
   * shouldDisplayApprovedTotal (which waits for ATTEND and doesn't exclude
   * CREATED) because the preapproved amount is exactly what the employee
   * wants to see right after preapproval (elevator-pitch#210).
   */
  get shouldDisplayApprovalAmounts(): boolean {
    return !PRE_APPROVAL_STATES.has(this.application.state);
  }

  /** Mirrors the legacy show screen's `shouldShowPaymentDetailsCard` gate. */
  get shouldShowPaymentDetailsCard(): boolean {
    return (
      this.application.shouldDisplayPaymentDetails && this.application.paymentsHistory.length > 0
    );
  }

  // Render the rail task label with the CourseCa$h™ brand mark styled. The
  // "use course cash" task is brand-only, so it renders the canonical mark
  // directly; "upload receipt" is brand + " receipt", so the mark is injected
  // into the localized string as a SafeString. Both emit HTML (htmlSafe).
  railTaskLabel(kind: AttentionTaskKind): string {
    const courseCash = this.intl.t('common.tas.course_cash.default', { htmlSafe: true });
    if (kind === 'use-course-cash') {
      return courseCash;
    }
    if (kind === 'upload-receipt') {
      return this.intl.t(RAIL_TASK_LABEL_KEYS[kind], { htmlSafe: true, courseCash });
    }
    return this.intl.t(RAIL_TASK_LABEL_KEYS[kind]);
  }

  get railTasks(): RailTaskRow[] {
    return this.attentionModel.tasks.map((task) => ({
      label: this.railTaskLabel(task.kind),
      statusKey: RAIL_STATUS_KEYS[task.status],
      icon: task.status === 'completed' ? PhCheck : PhClock,
      completed: task.status === 'completed',
    }));
  }

  get courses() {
    return this.application.activeCourses ?? [];
  }

  /** Newest comment for the Latest Comment card, with readable text + date. */
  get latestComment(): { text: string; date: string } | null {
    const comments = this.application.comments ?? [];
    const comment = comments[comments.length - 1];
    if (!comment) {
      return null;
    }
    const text = comment.isTasApproval ? comment.tasApprovalNote.body : (comment.comment ?? '');
    return { text, date: shortDate(comment.createdAt) };
  }

  close = () => {
    this.router.transitionTo('authenticated.tas.applications');
  };

  <template>
    <section
      class="tio-bg-surface tio-col gap-6 rounded-2xl border tio-border-default p-6"
      ...attributes
    >
      {{! Header }}
      <header class="tio-row-between items-start gap-4 border-b tio-border-default pb-4">
        <div class="min-w-0">
          <div class="tio-row flex-wrap gap-2">
            <h1 class="tio-text-xl-semibold tio-fg-primary">{{this.application.displayName}}</h1>
            <CourseCashTag @isDirectPayProgram={{this.isDirectPayProgram}} @size="sm" />
          </div>
          <p class="tio-text-sm-normal tio-fg-secondary mt-1">
            {{#if this.programName}}{{this.programName}}{{/if}}
            {{#if this.institutionName}}• {{this.institutionName}}{{/if}}
            {{#if this.submittedLabel}}• {{this.submittedLabel}}{{/if}}
          </p>
        </div>
        <Button
          @appearance="minimal"
          @icon={{PhX}}
          @iconSize={{20}}
          @ariaLabel={{t "tuition_assistance.applications.manage.header.close"}}
          class="shrink-0"
          {{on "click" this.close}}
        />
      </header>

      <div class="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {{! Main column }}
        <div class="tio-col gap-6 lg:col-span-2">
          {{! Self-gates: renders only for COURSES_NOT_APPROVED/COURSES_REJECTED/
            EVIDENCE_NOT_APPROVED/EVIDENCE_REJECTED. The "changes requested" banner +
            approver note + resubmit CTA that were missing on this screen (elevator-pitch
            evidence-submission feedback) — RejectionDetail already exists and is proven
            on the reimagined show page; this just wires it into manage too. }}
          <RejectionDetail @application={{this.application}} @onFix={{this.navigateToWizard}} />

          <NeedsYourAttention
            @model={{this.attentionModel}}
            @application={{this.application}}
            @onSubmitEvidence={{this.evidenceSubmissionBundle}}
            @onViewActivity={{this.openActivityDrawer}}
            @onUploadReceipt={{this.openReceiptModalForTask}}
          />

          {{! Request Summary }}
          <section class="tio-bg-surface rounded-2xl border tio-border-default p-5">
            <div class="tio-row-between items-baseline">
              <h2 class="tio-text-lg-semibold tio-fg-primary">
                {{t "tuition_assistance.applications.manage.summary.heading"}}
              </h2>
              <span class="tio-text-sm-normal tio-fg-secondary">
                {{t
                  "tuition_assistance.applications.manage.summary.items"
                  count=this.courses.length
                }}
              </span>
            </div>
            <ul class="tio-col mt-3 gap-2">
              {{#each this.courses as |course|}}
                <li class="tio-row-between gap-3 border-t tio-border-default pt-2">
                  <Button
                    @appearance="minimal"
                    data-test-manage-course-name
                    class="tio-text-sm-medium tio-fg-info underline truncate min-w-0 justify-start p-0"
                    {{on "click" (fn this.openCourseDrawer course)}}
                  >
                    {{course.displayName}}
                  </Button>
                  <span class="tio-text-sm-normal tio-fg-primary shrink-0">
                    {{formatCurrency (divideBy100 course.courseTuition)}}
                  </span>
                </li>
              {{/each}}
            </ul>
          </section>
        </div>

        {{! Right rail — one unified "Progress Indicator" card: Tasks · Request
            Details · Disbursement, separated by dividers (per the design). }}
        <aside class="tio-col gap-5">
          <section class="tio-bg-surface tio-col gap-5 rounded-2xl border tio-border-default p-5">
            {{! Tasks }}
            <div>
              <h2 class="tio-text-base-semibold tio-fg-primary">
                {{t "tuition_assistance.applications.manage.rail.tasks_heading"}}
                ({{this.attentionModel.pendingCount}})
              </h2>
              {{#if this.railTasks.length}}
                <dl class="tio-col mt-3 gap-2">
                  {{#each this.railTasks as |row|}}
                    <div class="tio-row-between gap-2">
                      <dt class="tio-text-sm-normal tio-fg-primary">{{row.label}}</dt>
                      <dd
                        class="tio-row gap-1 {{if row.completed 'tio-fg-info' 'tio-fg-secondary'}}"
                      >
                        <span class="tio-text-xs-normal">{{t row.statusKey}}</span>
                        <row.icon @size={{14}} />
                      </dd>
                    </div>
                  {{/each}}
                </dl>
              {{else}}
                <div class="tio-row-between mt-3 gap-2">
                  <span class="tio-text-sm-normal tio-fg-primary">
                    {{t "tuition_assistance.applications.manage.rail.none"}}
                  </span>
                  <span class="tio-text-xs-normal tio-fg-secondary">
                    {{if
                      this.isRejected
                      (t "tuition_assistance.applications.manage.rail.changes_requested")
                      (t "tuition_assistance.applications.manage.rail.in_review")
                    }}
                  </span>
                </div>
              {{/if}}
            </div>

            <div class="border-t tio-border-default"></div>

            {{! Request Details }}
            <div>
              <h2 class="tio-text-base-semibold tio-fg-primary">
                {{t "tuition_assistance.applications.manage.rail.request_details"}}
              </h2>
              <dl class="tio-col mt-3 gap-1">
                <div class="tio-row-between gap-2">
                  <dt class="tio-text-sm-normal tio-fg-secondary">
                    {{t "tuition_assistance.applications.manage.rail.dates"}}
                  </dt>
                  <dd class="tio-text-sm-normal tio-fg-primary">{{this.datesLabel}}</dd>
                </div>
                <div class="tio-row-between gap-2">
                  <dt class="tio-text-sm-normal tio-fg-secondary">
                    {{t "tuition_assistance.applications.manage.rail.expense_items"}}
                  </dt>
                  <dd class="tio-text-sm-normal tio-fg-primary">{{this.courses.length}}</dd>
                </div>
                <div class="tio-row-between gap-2">
                  <dt class="tio-text-sm-normal tio-fg-secondary">
                    {{t "tuition_assistance.applications.manage.rail.requested"}}
                  </dt>
                  <dd class="tio-text-sm-normal tio-fg-primary">{{this.requestedLabel}}</dd>
                </div>
                {{#if this.shouldDisplayApprovalAmounts}}
                  <div class="tio-row-between gap-2">
                    <dt class="tio-text-sm-normal tio-fg-secondary">
                      {{t "tuition_assistance.applications.manage.rail.pre_approved"}}
                    </dt>
                    <dd class="tio-text-sm-normal tio-fg-primary">{{this.preApprovedLabel}}</dd>
                  </div>
                  <div class="tio-row-between gap-2">
                    <dt class="tio-text-sm-normal tio-fg-secondary">
                      {{t "tuition_assistance.applications.manage.rail.final_approved"}}
                    </dt>
                    <dd class="tio-text-sm-normal tio-fg-primary">{{this.finalApprovedLabel}}</dd>
                  </div>
                {{/if}}
              </dl>
            </div>

            {{! Disbursement }}
            {{#if this.shouldShowPaymentDetailsCard}}
              <div class="border-t tio-border-default"></div>
              <div data-test-manage-disbursement>
                <h2 class="tio-text-base-semibold tio-fg-primary">
                  {{t "tuition_assistance.applications.manage.rail.disbursement"}}
                </h2>
                <div class="mt-3">
                  <PaymentDetailsCard
                    @showDetails={{true}}
                    @requestedTotal={{this.application.requestedTotal}}
                    @payments={{this.application.paymentsHistory}}
                    @isCreditBased={{this.application.isCreditBased}}
                    @totalCredits={{this.application.totalCredits}}
                  />
                </div>
              </div>
            {{/if}}
          </section>

          {{! Latest Comment — separate card }}
          {{#if this.latestComment}}
            <section class="tio-bg-surface rounded-2xl border tio-border-default p-5">
              <h2 class="tio-text-base-semibold tio-fg-primary">
                {{t "tuition_assistance.applications.manage.rail.latest_comment"}}
              </h2>
              <p class="tio-text-sm-normal tio-fg-primary mt-3 line-clamp-3">
                {{this.latestComment.text}}
              </p>
              {{#if this.latestComment.date}}
                <p class="tio-text-xs-normal tio-fg-secondary mt-1">{{this.latestComment.date}}</p>
              {{/if}}
            </section>
          {{/if}}
        </aside>
      </div>

      {{#if this.programTemplate}}
        <ShowCourseDrawer
          @isOpen={{this.isCourseDrawerOpen}}
          @onClose={{this.closeCourseDrawer}}
          @course={{this.courseShowing}}
          @programTemplate={{this.programTemplate}}
          @header={{this.courseDrawerHeader}}
        />
      {{/if}}

      <Drawer
        @isOpen={{this.isActivityDrawerOpen}}
        @onClose={{this.closeActivityDrawer}}
        @size="xl"
      >
        <div class="flex-none border-b tio-border-default px-6 pt-4 pb-3">
          <h2 class="tio-text-lg-semibold tio-fg-primary">
            {{t "tuition_assistance.course_cash.activity.drawer_title"}}
          </h2>
        </div>
        {{#if this.pcsCards.length}}
          <div class="h-full overflow-y-auto">
            {{#each this.pcsCardsWithMeta key="card.id" as |item|}}
              <ActivityView
                @card={{item.card}}
                @cardNumber={{item.cardNumber}}
                @totalCards={{item.totalCards}}
              />
            {{/each}}
          </div>
        {{else}}
          <DrawerBody>
            <p class="tio-text-sm-normal tio-fg-secondary py-4 italic">
              {{t "tuition_assistance.course_cash.activity.no_activity"}}
            </p>
          </DrawerBody>
        {{/if}}
      </Drawer>

      <ReceiptUploadModal
        @isOpen={{this.isReceiptModalOpen}}
        @transaction={{this.selectedReceiptTransaction}}
        @onClose={{this.closeReceiptModal}}
        @onUpload={{this.handleReceiptUpload}}
        @onRemoveReceipt={{this.handleRemoveReceipt}}
      />
    </section>
  </template>
}
